import { useI18n } from '@i18n/useI18n';
import { useEffect, useState, useRef } from 'react';
import { CardTitle, GridCard } from '@components/GridCards/Common.styled';
import {
  CardContent,
  CardHeader,
  ContentOverlay,
  GraphContent,
  GraphText,
  HeaderCol,
  LegendContainer,
  LegendItem,
  MainGraph,
  ProjectionContainer,
} from './AbsenceProjection.styled';
import Triangle_SVG from '@assets/svg/triangle_round.svg';
import ProjectionGraph from '../ProjectionGraph';
import SortOptionsProjection from '@components/GridCards/SortOptionsProjection';
import db from '@utils/db';
import { useStore } from '@store/appStore';
import UseOnScreen from '@components/Utils/useOnScreen';
import moment from 'moment';
import React from 'react';
import RainbowLoader from '@components/Loaders/RainbowLoader';

interface IProps {
  userId?: any;
  userSisId?: any;
  updateTime?: any;
}

export const AbsenceProjection = (props: IProps) => {
  const [show, setShow] = useState({ bestCase: true, worstCase: true });
  const [courses, setCourses] = useState(null);
  const { settings, filterDate, semesters } = useStore();
  const i18n = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = UseOnScreen(ref);
  const [visible, setVisible] = useState(false);
  const loadingData = useRef(false)
  const valid = useRef(true);
  const [sort, setSort] = useState<any>({ coursedisplayname: 'Alle fag', coursedescription: '' });
  const [dates, setDates] = useState([]);
  const [projection, setProjection] = useState<Array<any>>(null);
  const handleShow = (index) => {
    let s = JSON.parse(JSON.stringify(show));
    switch (index) {
      case 1:
        s.worstCase = !s.worstCase;
        break;
      case 2:
        s.bestCase = !s.bestCase;
        break;
    }
    setShow(s);
  };

  useEffect(() => {
    if (isVisible && !visible) setVisible(true)
  }, [isVisible])


  useEffect(() => {
    if (visible) {
      getData()
    }
  }, [visible, sort, props.userId])


  useEffect(() => {    
      updateCourses()
  }, [props.userId, props.updateTime])

  const updateCourses = async () => {
    let courses = (await db.Student_Stat_GetAllCoursesClassIDs(props.userSisId, filterDate.from, filterDate.to)) ?? []
    if (settings.USEMERIT) {
      let meritData = (await db.ListCoursesForStudent(props.userId, 30)) ?? [];
      let c = []
      for (let i = 0; i < courses.length; i++) {
        const course = courses[i];
        if (!meritData.some(cS => cS.sis_id === course.class_id && cS.active_merit && ((cS.coursedescription === null) || (cS.coursedescription === course.coursedescription)))) c.push(course)
      }
      setCourses(c)
    }
    else {
      setCourses(courses)
    }
  }

  async function getData() {
    let userId = props.userSisId;
    let now = moment();
    let dates = { from: '', to: '' };
    dates.from = filterDate.from;
    let semester = semesters.find((s) => s.text === filterDate.text);
    if (semester) dates.to = semester.toDate;
    else dates.to = filterDate.to;

    if (dates.to === null) dates.to = now.add(1, 'year').format('YYYY-MM-DD');
    if (dates.from === null) dates.from = '2021-08-01';
    if (moment(dates.to).isSame(now, 'date')) {
      valid.current = true;
      dates.to = now.add('12', 'month').format('YYYY-MM-DD');
    } else if (moment(dates.to).isBefore(now, 'date')) valid.current = false;
    else valid.current = true;

    let dayData: any[] = [];

    let dayData2: any[] = [];

    if (sort.coursedescription !== '') {
      dayData = (await db.Student_Stat_GetTotalPercentageDay(userId, filterDate.from, filterDate.to, sort.class_id, sort.coursedescription, settings.USEMERIT, settings.USEPAUSE)) ?? [];
      dayData2 = (await db.Student_Stat_GetTotalPercentageDay(userId, dates.from, dates.to, sort.class_id, sort.coursedescription, settings.USEMERIT, settings.USEPAUSE)) ?? [];
    } else {
      dayData = (await db.Student_Stat_GetTotalPercentageDay(userId, filterDate.from, filterDate.to, null, null, settings.USEMERIT, settings.USEPAUSE)) ?? [];
      dayData2 = (await db.Student_Stat_GetTotalPercentageDay(userId, dates.from, dates.to, null, null, settings.USEMERIT, settings.USEPAUSE)) ?? [];
    }

    let previousDate = now.add(-30, 'day');
    let sliceIndex = dayData.findIndex((d) => {
      let date = moment(d.date);
      return date.isSameOrAfter(previousDate, 'day');
    });

    let length = sliceIndex > -1 ? sliceIndex : dayData.length - 21;
    let tempDayData = dayData.slice(length);
    let tempDayData2 = dayData2.slice(length);

    let t = [
      {
        text: i18n.t('projection-current'),
        color: '#2A848E',
        dataset: tempDayData.map((item) => item.secondpercentage.toFixed(1)),
        hide: true,
      },
      {
        text: i18n.t('projection-worstcase'),
        color: '#FA505E',
        dataset: tempDayData2.map((item, index) => (index > tempDayData.length - 1 ? item.worsT_secondpercentage.toFixed(1) : item.secondpercentage.toFixed(1))),
      },
      {
        text: i18n.t('projection-bestcase'),
        color: '#2A848E',
        dataset: tempDayData2.map((item, index) => (index > tempDayData.length - 1 ? item.besT_secondpercentage.toFixed(1) : item.secondpercentage.toFixed(1))),
      },
    ];

    let d = tempDayData2.map((d) => moment(d.date).format('YYYY-MM-DD'));
    setDates(d);
    setProjection(t);
  }

  return (
    <GridCard ref={ref} col={2} heightAuto={true} overflowHidden={true}>
      <CardHeader>
        <HeaderCol>
          <LegendContainer>
            {projection?.map((ds, index) => {
              return ds.hide ? (
                <React.Fragment key={"legend_item_" + index}></React.Fragment>
              ) : (
                <LegendItem
                  onClick={() => handleShow(index)}
                  active={(index === 1 && show.worstCase) || (index === 2 && show.bestCase)}
                  color={ds.color}
                  key={'legend_item_' + index}
                >
                  <div />
                  <span>{ds.text}</span>
                </LegendItem>
              );
            })}
          </LegendContainer>
        </HeaderCol>
        <HeaderCol>
          <CardTitle>{i18n.t('projection-heading')}</CardTitle>
        </HeaderCol>
        <HeaderCol>
          <SortOptionsProjection setSort={setSort} courses={courses} />
        </HeaderCol>
      </CardHeader>
      <CardContent>
        {!valid ? (
          <ContentOverlay>
            <GraphText color="#ff0000">
              <h3>{i18n.t('projection-warning')}</h3>
            </GraphText>
          </ContentOverlay>
        ) : null}
        {projection === null ?
          <RainbowLoader />
          :
          <GraphContent>
            <ProjectionContainer>
              <ProjectionGraph show={show} dates={dates} dataPoints={projection} type={1} />
              <ProjectionGraph dates={dates} dataPoints={projection} />
            </ProjectionContainer>
          </GraphContent>
        }
      </CardContent>
    </GridCard>
  );
};

export default AbsenceProjection;